<template>
  <div class="product-gallery" v-if="images && images.length > 0">
    <nuxt-img
      v-if="images.length === 1 && images[0]"
      :src="images[0].url"
      class="product-gallery__image"
    />
    <SfCarousel
      ref="productGalleryCarousel"
      v-else
      :settings="sliderSettings"
      class="glide product-gallery__slider"
    >
      <template #prev="{ go }">
        <ChevronBlackDown
          class="product-gallery__slider-controls__prev"
          @click="go('prev')"
        />
      </template>
      <SfCarouselItem
        class="glide__slide product-gallery__slider__track__slides__slide"
        v-for="(image, index) in images"
        :key="index"
      >
        <nuxt-img
          :preload="index === 0"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :src="$device.isDesktop ? `${image.url}?w=960` : `${image.url}?w=768`"
          class="product-gallery__image"
        />
      </SfCarouselItem>
      <template #next="{ go }">
        <ChevronBlackDown
          class="product-gallery__slider-controls__next"
          @click="go('next')"
        />
      </template>
    </SfCarousel>
    <div class="product-gallery__zoom">
      <ZoomButton
        @click="
          openModal('pdp-gallery', {
            images: maxWidthImages,
            message,
            currentImageIndex,
          })
        "
      />
    </div>
    <div class="product-gallery__message" v-if="message">
      {{ message }}
    </div>
  </div>
  <nuxt-img
    class="product-gallery__placeholder"
    v-else
    src="/logo/logo-black.svg"
  />
</template>

<script>
import { computed, defineComponent } from '@nuxtjs/composition-api';
import { useUiState } from '~/composables';
import { useI18n } from '~/helpers/hooks/usei18n';
import { ZoomButton } from '~/components/General/Icons';
import { ChevronBlackDown } from '~/components/General/Icons';
import { SfCarousel } from '@storefront-ui/vue';

export default defineComponent({
  name: 'ProductGallery',
  components: {
    ZoomButton,
    ChevronBlackDown,
    SfCarousel,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  setup(props, { refs }) {
    const { openModal } = useUiState();
    const trans = useI18n();

    const message = computed(() =>
      props.product.configurable_product_options_selection?.media_gallery
        .length === 0
        ? trans.t('* THE PREVIEW OF THIS PRODUCT VARIANT IS NOT AVAILABLE')
        : ''
    );

    const variantGalleryImages = computed(() =>
      props.product.configurable_product_options_selection?.media_gallery
        ?.length > 0
        ? props.product.configurable_product_options_selection.media_gallery
        : []
    );

    const parentGalleryImages = computed(() =>
      props.product.media_gallery?.length > 0 ? props.product.media_gallery : []
    );

    const images = computed(() => {
      let filteredVariantGalleryImages = variantGalleryImages.value;
      parentGalleryImages.value.forEach((parentImage) => {
        filteredVariantGalleryImages = filteredVariantGalleryImages.filter(
          (variantImage) => variantImage.url !== parentImage.url
        );
      });
      return [...filteredVariantGalleryImages, ...parentGalleryImages.value]
        .filter((image) => !image?.roles?.includes('small_image'))
        .sort((a, b) => a.position - b.position);
    });
    const currentImageIndex = computed(
      () => refs?.productGalleryCarousel?.glide?.index ?? 0
    );

    const maxWidthImages = computed(() => {
      const bigImages = images.value;
      return bigImages.map((smallImage) => ({
        ...smallImage,
        url: `${smallImage.url}?f=a`,
      }));
    });

    const hasVariantMediaGallery = computed(
      () =>
        props.product.configurable_product_options_selection?.media_gallery
          ?.length > 0
    );

    const sliderSettings = {
      type: 'carousel',
      perView: 1,
      startAt: 0,
      gap: 0,
      peek: 0,
      breakpoints: {
        360: {
          perView: 1,
          peek: 0,
        },
        767: {
          perView: 1,
          peek: 0,
        },
        1023: {
          perView: 1,
          peek: 0,
        },
      },
    };

    const resetIndex = () => {
      refs?.productGalleryCarousel?.glide?.go('=0');
    };

    return {
      images,
      message,
      currentImageIndex,
      maxWidthImages,
      hasVariantMediaGallery,
      sliderSettings,
      openModal,
      resetIndex,
    };
  },
});
</script>

<style lang="scss">
.product-gallery {
  position: relative;
  aspect-ratio: 1/1;
  max-height: 100vw;
  &__placeholder {
    width: 100vw;
    aspect-ratio: 1/1;
  }
  &__image {
    width: 100%;
    height: 100vh;
    object-fit: contain;
    max-height: 100vw;
  }
  &__counter {
    display: none;
    position: absolute;
    left: 0.625rem;
    bottom: 0.625rem;
    @include label-s;
    text-transform: uppercase;
  }
  &__zoom {
    position: absolute;
    right: 0.625rem;
    bottom: 0.625rem;
    cursor: pointer;
  }
  &__message {
    position: absolute;
    left: 0.625rem;
    top: 0.625rem;
    @include label-s;
    text-transform: uppercase;
    max-width: 7.5rem;
  }
  .sf-carousel {
    &__controls {
      position: absolute;
      width: 90%;
      display: flex;
      justify-content: space-between;
      top: 50%;
      z-index: 4;
      left: 5%;
      .product-gallery__slider-controls__prev {
        transform: rotate(90deg);
      }
      .product-gallery__slider-controls__next {
        transform: rotate(270deg);
      }
      svg {
        cursor: pointer;
      }
    }
  }
  @include from-desktop-min {
    min-width: 50vw;
    width: 50%;
    &__placeholder {
      width: 50vw;
    }
    &__image {
      object-fit: contain;
    }
    &__counter {
      left: unset;
      right: 1.25rem;
      bottom: 1.25rem;
    }
    &__zoom {
      right: unset;
      left: 1.25rem;
      bottom: 1.25rem;
    }
    &__message {
      left: 1.25rem;
      top: 1.25rem;
      max-width: 13.75rem;
    }
    .sf-carousel {
      &__wrapper {
        max-width: 50vw;
        width: 50vw;
      }
    }
  }
}
</style>
